import { isCommentResolved, isCommentUnresolved } from "lib/comments";
import { createSelector } from "reselect";

import { resolveSaleStatus } from "lib/saleStatus";
import {
  getActiveRole,
  getSales,
  getSaleSubTypes,
  getSaleyards,
  getSaleStatuses,
  getSpecies,
} from "../root";
import { selectTemporaryBusinessCountByLivestockSaleIdLookup } from "../businesses";
import { selectCommentsByDeploymentSaleIdLookup } from "../comments";
import { selectSaleRoleNameByUserId } from "../saleRoles";
import {
  selectEffectiveEContractStatusBySaleIdLookup,
  selectEffectiveSaleTitleBySaleIdLookup,
} from "../sales";

export interface LivestockSaleAgGridRowType {
  livestockSale: {
    id: number;
    sale_type: string;
    saleSubType: SaleSubType;
    date: string;
    sale_title: string;
    species: Species;
    deploymentSaleId: number | null;
    saleCode: string;
    saleyard: Saleyard;
    status: string;
    managersLabel: string;
    assigneesLabel: string;
    managerNames: string[];
    assigneeNames: string[];
    eContractStatus: string | null;
    unresolvedBusinessCount: number;
  };
  resolvedCommentCount: number;
  unResolvedCommentCount: number;
}

export const selectSalesAggridData = createSelector(
  [
    getSales,
    getSpecies,
    getSaleSubTypes,
    selectCommentsByDeploymentSaleIdLookup,
    getSaleyards,
    getActiveRole,
    selectEffectiveSaleTitleBySaleIdLookup,
    selectEffectiveEContractStatusBySaleIdLookup,
    getSaleStatuses,
    selectSaleRoleNameByUserId,
    selectTemporaryBusinessCountByLivestockSaleIdLookup,
  ],
  (
    sales,
    species,
    saleSubTypes,
    commentsByDeploymentSaleId,
    saleYards,
    activeRole,
    effectiveSaleTitleBySaleIdLookup,
    effectiveEContractStatusBySaleIdLookup,
    saleStatuses,
    saleRoleNameByUserId,
    temporaryBusinessCountByLivestockSaleId,
  ) =>
    Object.values(sales).map((sale: Sale): LivestockSaleAgGridRowType => {
      const saleSubType = saleSubTypes[sale.sale_sub_type_id];

      // Comments are on a deployment sale, and only agency driven.   Only populate if there's just one!
      const deploymentSaleId =
        sale.deployment_sales.length === 1
          ? sale.deployment_sales[0].deployment_sale_id
          : null;
      const resolvedCommentCount = deploymentSaleId
        ? commentsByDeploymentSaleId[deploymentSaleId].filter(isCommentResolved)
            .length
        : 0;
      const unResolvedCommentCount = deploymentSaleId
        ? commentsByDeploymentSaleId[deploymentSaleId].filter(
            isCommentUnresolved,
          ).length
        : 0;

      // Group/sort value: custom status name, else archetype.
      const { archetype, customStatus } = resolveSaleStatus(
        sale,
        activeRole,
        saleStatuses,
      );
      const status = customStatus?.name ?? archetype;

      const managerNames = (sale.sale_attributes?.managerIds ?? [])
        .map(id => saleRoleNameByUserId[id])
        .filter(Boolean);
      const assigneeNames = (sale.sale_attributes?.assigneeIds ?? [])
        .map(id => saleRoleNameByUserId[id])
        .filter(Boolean);
      const managersLabel = managerNames.join(", ");
      const assigneesLabel = assigneeNames.join(", ");

      return {
        livestockSale: {
          id: sale.id,
          sale_type: sale.sale_type,
          saleSubType,
          date: sale.date,
          sale_title:
            effectiveSaleTitleBySaleIdLookup[sale.livestocksale_id] ?? "",
          species: species[sale.species_id],
          deploymentSaleId,
          saleCode: sale.sale_code,
          saleyard: saleYards[sale.saleyard_id],
          status,
          managersLabel,
          assigneesLabel,
          managerNames,
          assigneeNames,
          eContractStatus:
            effectiveEContractStatusBySaleIdLookup[sale.livestocksale_id] ??
            null,
          unresolvedBusinessCount:
            temporaryBusinessCountByLivestockSaleId[sale.livestocksale_id] ?? 0,
        },
        resolvedCommentCount,
        unResolvedCommentCount,
      };
    }),
);
